import { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router';
import { Alert, Badge, Button, Card, Form, ListGroup, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';

import {
  CreateSongpopMaestroDropMutation,
  CreateSongpopMaestroDropMutationVariables,
  GetSongpopMaestroDropsQuery,
  StartNewSongpopMaestroEventMutation,
  StartNewSongpopMaestroEventMutationVariables,
} from '../../../../../__gqltypes__';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { CREATE_SONGPOP_MAESTRO_DROP, SONGPOP_MAESTRO_DROPS_QUERY, START_NEW_SONGPOP_MAESTRO_EVENT } from './graphql';
import { DropData } from './components/DropData';
import { MaestroPackFeatureSwitch } from './components/MaestroPackFeatureSwitch';
import { MaestroPackSettings } from './components/MaestroPackSettings';
import { MaestroReminderSettings } from './components/MaestroReminderSettings';
import { dropStatusBadge, MaestroPackDrop, skuOptionLabel } from './utils';

const baseURL = '/sp3/liveops/songpop-maestro';

type EventTab = 'current' | 'past';

// Ended (its stop date is past), whatever its status: the Past events tab.
const isPast = (drop: MaestroPackDrop) => moment(drop.stopDate).isBefore(moment());

// The event opened by default in a tab: the one on sale now, else the latest one.
function defaultEvent(drops: MaestroPackDrop[]): MaestroPackDrop | undefined {
  return drops.find((drop) => dropStatusBadge(drop).label === 'Running') ?? drops[0];
}

const formatDate = (date: string | Date) => moment(date).format('YYYY-MM-DD HH:mm');

function EventList({
  drops,
  selectedId,
  onOpen,
}: {
  drops: MaestroPackDrop[];
  selectedId?: string;
  onOpen: (id: string) => void;
}) {
  return (
    <ListGroup className="mb-3">
      {drops.map((drop) => {
        const badge = dropStatusBadge(drop);
        const isOpen = drop.id === selectedId;
        return (
          <ListGroup.Item
            key={drop.id}
            action
            active={isOpen}
            onClick={() => onOpen(drop.id)}
            className="d-flex justify-content-between align-items-center"
          >
            <span>
              {drop.name}{' '}
              <small className={isOpen ? '' : 'text-muted'}>
                {formatDate(drop.startDate)} → {formatDate(drop.stopDate)} · {drop.seatsSold} sold
              </small>
            </span>
            <Badge pill variant={badge.variant}>
              {badge.label}
            </Badge>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );
}

/**
 * Songpop Maestro admin. On top of both tabs: the sales and perks switches.
 * - Current: what every owner gets for life (perks, instant rewards, rewarded ad zones), the founders wall, and the
 *   time-limited event on sale or coming (one live at a time; "Start new event" copies the latest).
 * - Past events: the ended ones, kept for their buyers (attribution, seats sold).
 */
function SongpopMaestro() {
  const { id: dropId } = useParams<{ id?: string }>();
  const history = useHistory();

  const { data, loading } = useQuery<GetSongpopMaestroDropsQuery>(SONGPOP_MAESTRO_DROPS_QUERY, {
    fetchPolicy: 'no-cache',
  });
  const [createDrop, { loading: creating, error: createError }] = useMutation<
    CreateSongpopMaestroDropMutation,
    CreateSongpopMaestroDropMutationVariables
  >(CREATE_SONGPOP_MAESTRO_DROP);
  const [startNewEvent, { loading: starting, error: startError }] = useMutation<
    StartNewSongpopMaestroEventMutation,
    StartNewSongpopMaestroEventMutationVariables
  >(START_NEW_SONGPOP_MAESTRO_EVENT);

  const [drops, setDrops] = useState<MaestroPackDrop[]>([]);
  const [tab, setTab] = useState<EventTab>('current');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSkuId, setNewSkuId] = useState('');

  const skuOptions = data?.liveOps.songpopMaestroSkus ?? [];
  const selectableSkus = skuOptions.filter((option) => !option.retired);
  const isFirstEvent = !loading && drops.length === 0;
  const currentEvents = drops.filter((drop) => !isPast(drop));
  const pastEvents = drops.filter(isPast);

  useEffect(() => {
    if (data) {
      setDrops(_.orderBy(data.liveOps.songpopMaestroDrops, 'startDate', 'desc'));
    }
  }, [data]);

  useEffect(() => {
    if (!newSkuId && selectableSkus.length > 0) {
      setNewSkuId(selectableSkus[0].id);
    }
  }, [newSkuId, selectableSkus]);

  const selectedDrop = drops.find((drop) => drop.id === dropId);
  // The tab follows the event in the URL (a link to an ended event opens Past events).
  useEffect(() => {
    if (selectedDrop) {
      setTab(isPast(selectedDrop) ? 'past' : 'current');
    }
  }, [selectedDrop]);
  // No event in the URL: the default one of the tab shown.
  const fallbackId = defaultEvent(tab === 'past' ? pastEvents : currentEvents)?.id;
  useEffect(() => {
    if (!selectedDrop && fallbackId) {
      history.replace(`${baseURL}/${fallbackId}`);
    }
  }, [selectedDrop, fallbackId, history]);

  const openEvent = (id: string) => history.push(`${baseURL}/${id}`);
  const switchTab = (key: string | null) => {
    const next: EventTab = key === 'past' ? 'past' : 'current';
    setTab(next);
    const fallback = defaultEvent(next === 'past' ? pastEvents : currentEvents);
    history.push(fallback ? `${baseURL}/${fallback.id}` : baseURL);
  };

  const upsertDrop = (drop: MaestroPackDrop) =>
    setDrops((previous) => _.orderBy([...previous.filter(({ id }) => id !== drop.id), drop], 'startDate', 'desc'));

  const openCreated = (drop: MaestroPackDrop | undefined) => {
    if (!drop) {
      return;
    }
    upsertDrop(drop);
    setShowNewModal(false);
    setNewName('');
    setTab('current');
    openEvent(drop.id);
  };

  const handleCreate = () => {
    const request = isFirstEvent
      ? createDrop({ variables: { input: { name: newName, skuId: newSkuId } } }).then(({ data: created }) =>
          openCreated(created?.createSongpopMaestroDrop)
        )
      : startNewEvent({ variables: { name: newName } }).then(({ data: started }) =>
          openCreated(started?.startNewSongpopMaestroEvent)
        );
    request.catch(() => {
      // surfaced through createError / startError
    });
  };

  // The editor of the opened event, in the tab it belongs to.
  const editor = (inTab: EventTab) =>
    selectedDrop && isPast(selectedDrop) === (inTab === 'past') ? (
      <DropData
        drop={selectedDrop}
        skuOptions={skuOptions}
        otherDrops={drops}
        onSaved={upsertDrop}
        onDeleted={(deletedId) => {
          setDrops((previous) => previous.filter(({ id }) => id !== deletedId));
          history.push(baseURL);
        }}
      />
    ) : null;

  return (
    <>
      <LoadingLogo show={loading} />
      <MaestroPackFeatureSwitch />

      <Tabs activeKey={tab} onSelect={switchTab} className="mb-3">
        <Tab eventKey="current" title="Current">
          <MaestroPackSettings section="benefits" />
          <MaestroPackSettings section="foundersWall" />

          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Event</strong>
              <Button variant="success" size="sm" onClick={() => setShowNewModal(true)}>
                <i className="fas fa-plus mr-2" />
                {isFirstEvent ? 'Create the first event' : 'Start new event'}
              </Button>
            </Card.Header>
            <Card.Body>
              <MaestroPackSettings section="audience" />
              {/* several only when the next one is already prepared */}
              {currentEvents.length > 1 && (
                <EventList drops={currentEvents} selectedId={selectedDrop?.id} onOpen={openEvent} />
              )}
              {editor('current') ??
                (!loading && <p className="text-muted mb-0">No event on sale or coming: start a new one.</p>)}
            </Card.Body>
          </Card>

          {/* Not tied to one event: it reminds about whichever event is on sale for the player. */}
          <MaestroReminderSettings />
        </Tab>

        <Tab eventKey="past" title={`Past events (${pastEvents.length})`}>
          {pastEvents.length === 0 ? (
            <p className="text-muted">No event has ended yet.</p>
          ) : (
            <>
              <EventList drops={pastEvents} selectedId={selectedDrop?.id} onOpen={openEvent} />
              {editor('past')}
            </>
          )}
        </Tab>
      </Tabs>

      <Modal show={showNewModal} onHide={() => setShowNewModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isFirstEvent ? 'First event' : 'New event'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(createError || startError) && <Alert variant="danger">{(createError || startError)?.message}</Alert>}
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control value={newName} onChange={(e) => setNewName(e.target.value)} />
          </Form.Group>
          {isFirstEvent ? (
            <Form.Group>
              <Form.Label>Price point</Form.Label>
              <Form.Control as="select" value={newSkuId} onChange={(e) => setNewSkuId(e.target.value)}>
                {selectableSkus.map((option) => (
                  <option key={option.id} value={option.id}>
                    {skuOptionLabel(option)}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          ) : (
            <p className="text-muted">
              Copies the latest event (price, discount, cap, audience, pushes), starting when it ends for the same
              duration. Nothing sold and no push sent yet.
            </p>
          )}
          <p className="text-muted mb-0">Created as DRAFT: nobody sees it until you set it ACTIVE.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={creating || starting || !newName.trim() || (isFirstEvent && !newSkuId)}
            onClick={handleCreate}
          >
            {creating || starting ? <Spinner animation="border" size="sm" /> : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SongpopMaestro;
